import React, { useState } from "react";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useLang } from "../contexts/LanguageContext";
import { submitQuote } from "../lib/api";
import { toast } from "sonner";

const CartDrawer = () => {
  const { open, setOpen, items, updateQty, remove, subtotal, clear } = useCart();
  const { t } = useLang();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", notes: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await submitQuote({
        ...form,
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity }))
      });
      toast.success(t.shop.quoteSent);
      clear();
      setShowForm(false);
      setOpen(false);
      setForm({ name: "", email: "", company: "", phone: "", notes: "" });
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[480px] bg-[#FFF8F0] border-l border-border flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="display-serif text-2xl">{t.shop.yourCart}</h3>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} aria-label="Close cart"><X className="w-5 h-5" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8 text-foreground/60">{t.shop.empty}</div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 pb-6 border-b border-border last:border-0">
                  {i.image && <img src={i.image} alt={i.name} className="w-20 h-24 object-cover" />}
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <div>
                        <div className="display-serif text-lg leading-tight">{i.name}</div>
                        {i.size && <div className="text-xs text-foreground/50 mt-1">{i.size}</div>}
                      </div>
                      <button onClick={() => remove(i.product_id)} data-testid={`remove-${i.product_id}`} aria-label="Remove" className="text-foreground/40 hover:text-[#F97316]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button data-testid={`qty-dec-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-foreground hover:text-background transition-colors"><Minus className="w-3 h-3" /></button>
                        <span className="w-8 text-center text-sm">{i.quantity}</span>
                        <button data-testid={`qty-inc-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-foreground hover:text-background transition-colors"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-6 space-y-4">
              {!showForm ? (
                <button data-testid="checkout-open-btn" onClick={() => setShowForm(true)} className="btn-primary w-full">{t.shop.checkout}</button>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="quote-form">
                  <input data-testid="quote-name" required placeholder={t.contact.name} className="input-clean" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="quote-email" type="email" required placeholder={t.contact.email} className="input-clean" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input data-testid="quote-company" placeholder={t.contact.company} className="input-clean" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  <input data-testid="quote-phone" placeholder={t.contact.phone} className="input-clean" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <textarea data-testid="quote-notes" rows={2} placeholder={t.contact.message} className="input-clean resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  <button data-testid="quote-submit-btn" type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60">
                    {submitting ? "..." : t.common.submit}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
